/**
 * GraphQL error messages that should not be reported to Sentry.
 * These are expected application-level errors (auth, validation, not-found).
 */
const IGNORED_GRAPHQL_MESSAGES = [
    'Access denied',
    'Unauthenticated',
    'Not found',
    'Validation failed',
    'validation',
    'The given data was invalid',
    'Internal server error',
    'This action is unauthorized',
]

export default ({ graphQLErrors, networkError, operation }, nuxtContext) => {
    if (graphQLErrors) {
        handleGraphQLErrors(graphQLErrors, operation, nuxtContext)
        return
    }

    if (networkError) {
        handleNetworkErrors(networkError, operation, nuxtContext)
    }
}

/**
 * Handles GraphQL errors.
 *
 * @param {Array} graphQLErrors - Array of GraphQL errors.
 * @param {object} operation - GraphQL operation details.
 * @param {object} nuxtContext - Nuxt context.
 */
function handleGraphQLErrors(graphQLErrors, operation, nuxtContext) {
    const errorMessage = graphQLErrors?.[0]?.message

    if (errorMessage?.includes('Access denied! Must be logged in')) {
        redirectToLogin()
        return
    }

    // Check if the error has userErrors (validation) — these are expected
    const hasUserErrors = graphQLErrors.some(
        (err) => err?.extensions?.category === 'validation' || err?.extensions?.userErrors
    )

    if (hasUserErrors) {
        showErrorMessage(errorMessage)
        return
    }

    showErrorMessage(errorMessage)

    // Don't report expected application errors to Sentry
    if (isIgnoredGraphQLError(errorMessage)) {
        return
    }

    nuxtContext.$sentry.withScope((scope) => {
        scope.setTag('errorType', 'GraphQL Error')
        scope.setTag('graphql.operation', operation.operationName || 'unknown')
        scope.setFingerprint(['graphql', operation.operationName || 'unknown', errorMessage])
        scope.setContext('GraphQL Details', {
            rawError: JSON.stringify(graphQLErrors),
            operationName: operation.operationName,
            variables: sanitizeVariables(operation.variables),
            query: operation.query?.loc?.source?.body,
        })
        nuxtContext.$sentry.captureException(new Error(errorMessage))
    })
}

/**
 * Checks if a GraphQL error message should be ignored by Sentry.
 *
 * @param {string|null} message - The error message.
 * @returns {boolean}
 */
function isIgnoredGraphQLError(message) {
    if (!message) {
        return false
    }

    return IGNORED_GRAPHQL_MESSAGES.some((ignored) =>
        message.toLowerCase().includes(ignored.toLowerCase())
    )
}

/**
 * Removes sensitive fields from GraphQL variables before sending to Sentry.
 *
 * @param {object} variables - GraphQL operation variables.
 * @returns {object}
 */
function sanitizeVariables(variables) {
    if (!variables) {
        return variables
    }

    const sensitiveKeys = ['password', 'token', 'secret', 'credit_card', 'creditCard', 'cvv', 'iban']
    const sanitized = { ...variables }

    for (const key of Object.keys(sanitized)) {
        if (sensitiveKeys.some((s) => key.toLowerCase().includes(s))) {
            sanitized[key] = '[REDACTED]'
        }
    }

    return sanitized
}

/**
 * Handles network errors.
 *
 * @param {object} networkError - Network error object.
 * @param {object} operation - GraphQL operation details.
 * @param {object} nuxtContext - Nuxt context.
 */
function handleNetworkErrors(networkError, operation, nuxtContext) {
    const { statusCode } = networkError

    if (!statusCode) {
        return
    }

    if (statusCode === 503) {
        // The website is in maintenance mode - refresh to show maintenance page
        showErrorMessage(window?.$nuxt?.$t('messages.maintenance'))

        // reload after 10 seconds
        setTimeout(() => {
            window.location.reload()
        }, 10000)

        return
    }

    if (statusCode >= 500 && statusCode <= 599) {
        showErrorMessage()
        return
    }

    switch (statusCode) {
        case 401:
        case 403:
            redirectToLogin()
            break
        case 419:
            window.location.reload()
            break
        case 429:
            handleRateLimitError()
            break
        default:
            captureUnhandledError(networkError, operation, nuxtContext)
            break
    }
}

/**
 * Redirects the user to the login page and reloads the page.
 */
function redirectToLogin() {
    window.location.hash = '#login'
    window.location.reload()
}

/**
 * Handles rate limit errors by reloading the page when visibility changes.
 */
function handleRateLimitError() {
    const reload = () => {
        if (!document.hidden) {
            window.location.reload()
        }

        // Remove the event listener to prevent multiple reloads
        document.removeEventListener('visibilitychange', reload)
    }

    document.addEventListener('visibilitychange', reload)
}

/**
 * Captures unhandled network errors using Sentry.
 *
 * @param {object} networkError - Network error object.
 * @param {object} operation - GraphQL operation details.
 * @param {object} nuxtContext - Nuxt context.
 */
function captureUnhandledError(networkError, operation, nuxtContext) {
    const error = new Error(
        `GraphQL network error: ${networkError.statusCode} on ${operation.operationName || 'unknown'}`
    )

    nuxtContext.$sentry.withScope((scope) => {
        scope.setTag('errorType', 'GraphQL Network Error')
        scope.setTag('http.status_code', networkError.statusCode)
        scope.setTag('graphql.operation', operation.operationName || 'unknown')
        scope.setFingerprint(['graphql-network', String(networkError.statusCode), operation.operationName || 'unknown'])
        scope.setContext('GraphQL Details', {
            rawError: JSON.stringify(networkError),
            operationName: operation.operationName,
            variables: sanitizeVariables(operation.variables),
            query: operation.query?.loc?.source?.body,
            url: window.location.href,
        })
        nuxtContext.$sentry.captureException(error)
    })
}

/**
 * Shows a specific or the default error message.
 *
 * @param {string|null} errorMessage - Error message to display.
 */
function showErrorMessage(errorMessage = null) {
    const errorBox = document?.getElementById('gql-critical-error-message')

    if (!errorBox) {
        return
    }

    errorBox.textContent =
        errorMessage || window?.$nuxt?.$t('messages.critical_gql_error') || 'Something went wrong, please try again later.'
    errorBox.style.display = 'block'
}
